import React from 'react';
import { SelectValue } from 'antd/lib/select';
import { useTranslation } from 'react-i18next';

import { GuardrailSettingsState, RegexCheckSettings, RegexFlag } from '../../../../../../gamma-api';
import IbSelect from '../../../../../components/common/IbSelect';
import IbTypography from '../../../../../components/common/IbTypography';
import RegexPatternsEditor from '../../../../../components/common/RegexPatternsEditor';
import { ITEM_CLASS_NAME, SECTION_CLASS_NAME } from '../../../../../components/common/TypedStepModal/const';
import { UseValidationForm } from '../../../../../utils/validationUtil';
import { getRegexFlagLabel, ICheckStep } from '../../shared/utils';
import { CheckValidationField, CheckValidationForm } from '../CheckModal.validation';

import {
  CheckBlockMessageField,
  CheckEnabledField,
  CheckNameField,
  CheckUseOriginalTextForCheckField,
} from './CommonCheckParameters';
import CheckBehaviorField from './CheckBehaviorField';

export interface IRegexCheckParametersProps {
  step: ICheckStep;
  validationFieldRefs: Partial<Record<CheckValidationField, React.RefObject<HTMLDivElement>>>;
  validationForm: UseValidationForm<CheckValidationForm>;
  onChange: (patch: Partial<RegexCheckSettings>) => void;
}

const RegexCheckParameters: React.FC<IRegexCheckParametersProps> = ({
  step,
  validationFieldRefs,
  validationForm,
  onChange,
}) => {
  const { t } = useTranslation();
  const check = step.check as RegexCheckSettings;
  const { validationState, onFieldChange, onFieldBlur, getShowError, getErrorMessage } = validationForm;
  const showPatternsError = getShowError(validationState.patterns);

  const flagOptions = [RegexFlag.IgnoreCase, RegexFlag.Multiline, RegexFlag.DotAll, RegexFlag.Ascii].map((value) => ({
    value,
    label: getRegexFlagLabel(value),
  }));

  const onStateChange = (state: GuardrailSettingsState) => {
    onChange({ state });
    onFieldChange('state', state);
  };

  const onPatternsChange = (patterns: string[]) => {
    onChange({ patterns });
    onFieldChange('patterns', patterns);
  };

  const onPatternsBlur = () => onFieldBlur('patterns');

  const onFlagsChange = (value: SelectValue) => {
    const flags = value as RegexFlag[];
    onChange({ flags });
    onFieldChange('flags', flags);
  };

  return (
    <div className={SECTION_CLASS_NAME}>
      <CheckEnabledField state={check.state} onChange={onStateChange} />
      <CheckNameField check={check} onChange={onChange} />
      <div ref={validationFieldRefs.patterns} className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: RegexCheck: Patterns: Title')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: RegexCheck: Patterns: Description')}
        </IbTypography.Paragraph>
        <RegexPatternsEditor
          hasError={showPatternsError}
          patterns={check.patterns}
          onBlur={onPatternsBlur}
          onChange={onPatternsChange}
        />
        {showPatternsError && (
          <IbTypography.Paragraph error type="descriptor">
            {getErrorMessage(validationState.patterns, t('Agent: Guardrails: RegexCheck: Patterns: Error'))}
          </IbTypography.Paragraph>
        )}
      </div>
      <CheckBehaviorField value={check.behavior} onChange={(behavior) => onChange({ behavior })} />
      <CheckBlockMessageField check={check} onChange={onChange} />
      <CheckUseOriginalTextForCheckField check={check} onChange={onChange} />
      <div className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: RegexCheck: Flags: Title')}
        </IbTypography.Paragraph>
        <IbSelect mode="multiple" options={flagOptions} value={check.flags} onChange={onFlagsChange} />
      </div>
    </div>
  );
};

export default RegexCheckParameters;
